import type { ServiceDef } from './services';

/** Outdoor specialty services mirrored from HN nav (decks, covered patios, pergolas, outdoor kitchens). */
export const OUTDOOR_SPECIALTY_SERVICES: ServiceDef[] = [
  {
    slug: 'deck-builders',
    name: 'Deck Builders',
    shortName: 'Deck',
    eyebrow: 'Texas Deck Builders',
    heroTitle: 'Deck builders across Texas',
    heroLead:
      'Cedar, composite, and hardwood decks planned for Texas heat, sun, and storms. Request a free on-site estimate for your yard.',
    heroImage:
      'https://images.unsplash.com/photo-1600585153490-76fb20a32601?w=1920&q=80&auto=format&fit=crop',
    heroImageAlt: 'Elevated wood deck with outdoor seating',
    metaTitle: 'Deck Builders in Texas | Cost, Materials & Quotes | Epic Home Pros',
    metaDescription:
      'Free deck building estimates in Texas. Compare cedar, composite, and hardwood options, cost factors, and process steps for Austin, Dallas, Houston, San Antonio, and nearby metros.',
    introTitle: 'Custom decks for how Texans actually use a backyard',
    introParagraphs: [
      'A deck can be a simple ground-level platform or an elevated, multi-level structure that works with slope, drainage, and how you entertain. Material choice and substructure detailing matter as much as the finish you see.',
      'Texas sun, heat, and rain punish shortcuts. Footings, joist spacing, flashing, fasteners, and airflow under the deck all affect how long the surface stays flat and solid.',
      'Epic Home Pros helps homeowners across major Texas metros start with one request, an on-site yard visit, and a written estimate. You decide whether to move forward.',
    ],
    projectTypesTitle: 'Deck projects we commonly handle',
    projectTypes: [
      'Ground-level platforms and walk-out decks',
      'Elevated and multi-level decks on sloped lots',
      'Cedar, composite, and hardwood decking',
      'Railings, stairs, and built-in seating',
      'Lighting and prep for a future cover or pergola',
    ],
    whyTitle: 'Why homeowners start a deck quote here',
    whyItems: [
      'One request instead of shopping cold-call lists.',
      'On-site walk for sun, drainage, slope, and access.',
      'Material options compared for Texas weather and maintenance.',
      'Written estimate after the visit - free, no obligation.',
      'Reply within one business day.',
    ],
    processTitle: 'How a deck quote works',
    processSteps: [
      {
        label: '1. Request',
        body: 'Share your city, rough size, preferred material if you have one, and photos of the yard.',
      },
      {
        label: '2. On-site visit',
        body: "We'll schedule a visit to walk slope, drainage, access, and how you want to use the space.",
      },
      {
        label: '3. Written estimate',
        body: 'You receive a written estimate based on the site - not a catalog photo. You decide next steps.',
      },
    ],
    galleryEyebrow: 'Decks',
    galleryTitle: 'Platforms built for Texas backyards.',
    gallery: [
      {
        src: 'https://images.unsplash.com/photo-1600585153490-76fb20a32601?w=1200&q=80&auto=format&fit=crop',
        alt: 'Wood deck with seating',
      },
      {
        src: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=1200&q=80&auto=format&fit=crop',
        alt: 'Outdoor living deck area',
      },
      {
        src: 'https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=1200&q=80&auto=format&fit=crop',
        alt: 'Covered outdoor deck space',
      },
    ],
    cost: {
      title: 'Typical Texas deck cost context (2026)',
      intro:
        'These are planning ranges for Texas market context in 2026, not a quote for your yard. Size, height, material, railings, and site conditions move the number. Your written estimate after an on-site visit is what matters.',
      rows: [
        {
          tier: 'Modest ground-level',
          cost: '$8,000 - $18,000',
          includes: 'Smaller cedar or composite platforms with straightforward access.',
        },
        {
          tier: 'Elevated / multi-level',
          cost: '$18,000 - $35,000',
          includes: 'Taller framing, deeper footings, stairs, and code railings.',
        },
        {
          tier: 'Premium features',
          cost: '$35,000+',
          includes: 'Hardwood decking, built-in seating, lighting, or prep for a cover.',
        },
      ],
    },
    faqs: [
      {
        question: 'How much does a deck cost in Texas?',
        answer:
          'As of 2026, many modest ground-level decks land around $8,000-$18,000, while elevated or multi-level builds often run $18,000-$35,000+. Material, height, and railings are the big swingers. A written on-site estimate prices your yard.',
      },
      {
        question: 'Composite vs. cedar vs. hardwood - which is better in Texas?',
        answer:
          'Composite is popular for lower maintenance in strong sun. Cedar gives a natural look with periodic sealing. Dense hardwoods last longest and cost more. An on-site visit can compare options for your exposure and budget.',
      },
      {
        question: 'Do I need a permit for a deck?',
        answer:
          'Often yes for elevated decks or larger platforms. Requirements vary by city. The estimate visit is a good time to talk through what your municipality typically requires.',
      },
      {
        question: 'Can you build on a sloped lot?',
        answer:
          'Yes. Elevated and multi-level decks are common on sloped Texas lots. Footings, framing, and railings need to match the grade - which is why an on-site walk matters.',
      },
      {
        question: 'Is the deck estimate free?',
        answer: 'Yes. Free on-site estimate with no obligation to move forward.',
      },
    ],
    ctaTitle: 'Ready for a deck quote?',
    ctaLead: 'Free on-site estimate. No obligation.',
    showInNav: true,
  },
  {
    slug: 'covered-patios',
    name: 'Covered Patios',
    shortName: 'Covered Patio',
    eyebrow: 'Texas Covered Patios',
    heroTitle: 'Covered patios for Texas sun and rain',
    heroLead:
      'Attached and freestanding covered patios that make the backyard usable in July heat and summer storms. Free on-site estimates across major Texas metros.',
    heroImage:
      'https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=1920&q=80&auto=format&fit=crop',
    heroImageAlt: 'Covered patio with outdoor seating',
    metaTitle: 'Covered Patios in Texas | Quotes & Planning | Epic Home Pros',
    metaDescription:
      'Free covered patio estimates in Texas. Learn attached vs freestanding options, roof choices, cost factors, and process steps for major Texas metros.',
    introTitle: 'Shade that makes a Texas backyard usable year-round',
    introParagraphs: [
      'A covered patio turns an exposed slab or bare yard into a place you can use in heat and rain. Projects range from a straightforward patio cover to a finished outdoor room with fans, lighting, and seating.',
      'Roof type, attachment to the house, electrical for fans and lights, and whether a new slab is needed all shape scope and cost. Matching pitch and materials to the home keeps the addition from looking bolted on.',
      'Start with a quote request and an on-site visit. You get a written estimate and decide whether to move forward.',
    ],
    projectTypesTitle: 'Covered patio projects we commonly handle',
    projectTypes: [
      'Attached patio covers tied into the home',
      'Freestanding covers for pool areas or detached zones',
      'Shingle, metal, and insulated roof options',
      'Ceiling fans, recessed lighting, and outlets',
      'Integration with outdoor kitchens or seating layouts',
    ],
    whyTitle: 'Why start here',
    whyItems: [
      'Clear discussion of attached vs freestanding options.',
      'On-site look at roof lines, drainage, and electrical needs.',
      'Planning for Texas heat - not just fair-weather shade.',
      'Written estimate. Free. No obligation.',
      'Coverage across Austin, Dallas, Houston, San Antonio, and nearby metros.',
    ],
    processTitle: 'How a covered patio quote works',
    processSteps: [
      {
        label: '1. Request',
        body: 'Tell us city, attached vs freestanding preference, and whether you want fans, lighting, or an outdoor kitchen connection.',
      },
      {
        label: '2. On-site visit',
        body: 'We review the house connection, slab condition, drainage, and how you want to use the space.',
      },
      {
        label: '3. Written estimate',
        body: 'You receive a written estimate for the cover and related work. You decide next steps.',
      },
    ],
    galleryEyebrow: 'Covered patios',
    galleryTitle: 'Shade built for Texas summers.',
    gallery: [
      {
        src: 'https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=1200&q=80&auto=format&fit=crop',
        alt: 'Covered patio',
      },
      {
        src: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=1200&q=80&auto=format&fit=crop',
        alt: 'Outdoor lounge under cover',
      },
      {
        src: 'https://images.unsplash.com/photo-1600585153490-76fb20a32601?w=1200&q=80&auto=format&fit=crop',
        alt: 'Deck adjacent to covered area',
      },
    ],
    cost: {
      title: 'Typical Texas covered patio cost context (2026)',
      intro:
        'Planning ranges for Texas market context in 2026 - not a quote for your house. Size, roof type, electrical, and slab work move the number.',
      rows: [
        {
          tier: 'Basic attached cover',
          cost: '$12,000 - $25,000',
          includes: 'Straightforward patio cover for shade and rain protection.',
        },
        {
          tier: 'Finished outdoor room',
          cost: '$25,000 - $45,000',
          includes: 'Insulated or upgraded roof, fans, lighting, finished ceiling details.',
        },
        {
          tier: 'With kitchen / fireplace add-ons',
          cost: '$45,000+',
          includes: 'Cover plus outdoor kitchen, fireplace, or major electrical/gas work.',
        },
      ],
    },
    faqs: [
      {
        question: 'Attached vs freestanding - which should I choose?',
        answer:
          'Attached covers usually look most seamless with the house. Freestanding structures work well for pool areas or shaded spots away from the home. An on-site visit can compare both for your lot.',
      },
      {
        question: 'Will a covered patio help in Texas heat?',
        answer:
          'Yes. Blocking direct sun helps immediately. Insulated roofs and ceiling fans improve summer comfort further so the space stays usable in peak heat.',
      },
      {
        question: 'Do covered patios usually need a permit?',
        answer:
          'Attached structural covers often do. Requirements vary by city. We can talk through typical permitting expectations during the estimate visit.',
      },
      {
        question: 'Can lighting and fans be included?',
        answer:
          'Yes. Planning electrical during the build is cleaner than adding it later. Share those goals in the quote request.',
      },
      {
        question: 'Is the estimate free?',
        answer: 'Yes. Free on-site estimate with no obligation.',
      },
    ],
    ctaTitle: 'Ready for a covered patio quote?',
    ctaLead: 'Free on-site estimate. No obligation.',
    showInNav: true,
  },
  {
    slug: 'pergolas',
    name: 'Pergolas',
    shortName: 'Pergola',
    eyebrow: 'Texas Pergolas',
    heroTitle: 'Pergolas for real Texas shade',
    heroLead:
      'Cedar, steel, and louvered pergola options - attached or freestanding - planned for sun, wind, and how you use the yard. Free on-site estimates.',
    heroImage:
      'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=1920&q=80&auto=format&fit=crop',
    heroImageAlt: 'Outdoor pergola shade structure',
    metaTitle: 'Pergolas in Texas | Shade Structures & Quotes | Epic Home Pros',
    metaDescription:
      'Free pergola estimates in Texas. Compare cedar, steel, and louvered options, shade strategies, cost factors, and process steps across major Texas metros.',
    introTitle: 'Open-air shade without a full enclosure',
    introParagraphs: [
      'A pergola defines an outdoor room and adds shade without fully enclosing the space. Fixed slats, denser spacing, or adjustable louvers each deliver different coverage - important under Texas sun.',
      'Material choice (cedar, steel, aluminum/louvered systems), attached vs freestanding placement, and whether lighting or fans are included all shape the project.',
      'Request a quote and schedule an on-site visit. You get a written estimate and decide whether to move forward.',
    ],
    projectTypesTitle: 'Pergola projects we commonly handle',
    projectTypes: [
      'Freestanding pergolas over patios or lawns',
      'Attached pergolas tied to the home',
      'Cedar and steel frames',
      'Louvered or adjustable shade systems',
      'Integrated lighting and fan-ready layouts',
    ],
    whyTitle: 'Why start here',
    whyItems: [
      'Shade strategy planned for July - not just spring photos.',
      'On-site review of sun path, wind, and anchoring needs.',
      'Clear comparison of open rafters vs denser shade options.',
      'Written estimate. Free. No obligation.',
      'Service across major Texas metros.',
    ],
    processTitle: 'How a pergola quote works',
    processSteps: [
      {
        label: '1. Request',
        body: 'Share city, approximate size, attached vs freestanding, and the shade level you want.',
      },
      {
        label: '2. On-site visit',
        body: 'We walk sun exposure, anchoring conditions, and how the pergola should relate to patio, deck, or pool.',
      },
      {
        label: '3. Written estimate',
        body: 'You receive a written estimate for structure and options. You decide next steps.',
      },
    ],
    galleryEyebrow: 'Pergolas',
    galleryTitle: 'Shade with an open feel.',
    gallery: [
      {
        src: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=1200&q=80&auto=format&fit=crop',
        alt: 'Pergola outdoor lounge',
      },
      {
        src: 'https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=1200&q=80&auto=format&fit=crop',
        alt: 'Covered outdoor structure',
      },
      {
        src: 'https://images.unsplash.com/photo-1600585153490-76fb20a32601?w=1200&q=80&auto=format&fit=crop',
        alt: 'Deck with shade structure nearby',
      },
    ],
    cost: {
      title: 'Typical Texas pergola cost context (2026)',
      intro:
        'Planning ranges for Texas market context in 2026 - not a quote for your yard. Size, material, roof style, and attachments move the number.',
      rows: [
        {
          tier: 'Cedar freestanding',
          cost: '$5,000 - $12,000',
          includes: 'Straightforward wood pergola with classic open or fixed-slat shade.',
        },
        {
          tier: 'Steel / custom',
          cost: '$8,000 - $20,000',
          includes: 'Stronger spans, modern profiles, larger footprints.',
        },
        {
          tier: 'Louvered / premium',
          cost: '$15,000 - $40,000+',
          includes: 'Adjustable louvers, motorization, integrated lighting, premium systems.',
        },
      ],
    },
    faqs: [
      {
        question: 'Pergola vs covered patio - what is the difference?',
        answer:
          'A pergola provides partial-to-adjustable shade with an open feel. A covered patio has a solid waterproof roof for full rain protection. Choose based on whether you need to stay dry or prefer dappled shade.',
      },
      {
        question: 'Do pergolas need permits in Texas cities?',
        answer:
          'Freestanding structures sometimes do not; attached pergolas or those with electrical often do. Rules vary by city. Ask during the estimate visit.',
      },
      {
        question: 'Will a pergola actually shade an Austin or Houston summer?',
        answer:
          'Open rafters give light shade. Closer slats give more. Adjustable louvers or canopies can approach full shade on demand. Orientation and spacing matter - plan them on site.',
      },
      {
        question: 'Cedar, steel, or aluminum?',
        answer:
          'Cedar is classic and warmer looking with maintenance. Steel spans farther with a modern look. Aluminum louvers are low-maintenance and flexible for shade control. Compare during the visit.',
      },
      {
        question: 'Is the estimate free?',
        answer: 'Yes. Free on-site estimate with no obligation.',
      },
    ],
    ctaTitle: 'Ready for a pergola quote?',
    ctaLead: 'Free on-site estimate. No obligation.',
    showInNav: true,
  },
  {
    slug: 'outdoor-kitchens',
    name: 'Outdoor Kitchens',
    shortName: 'Outdoor Kitchen',
    eyebrow: 'Texas Outdoor Kitchens',
    heroTitle: 'Outdoor kitchens built for Texas entertaining',
    heroLead:
      'Built-in grills, weatherproof counters, and real utilities planned with your patio or pergola. Free on-site estimates across major Texas metros.',
    heroImage:
      'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=1920&q=80&auto=format&fit=crop',
    heroImageAlt: 'Outdoor kitchen and entertaining space',
    metaTitle: 'Outdoor Kitchens in Texas | Quotes & Planning | Epic Home Pros',
    metaDescription:
      'Free outdoor kitchen estimates in Texas. Plan grills, counters, utilities, cost factors, and process steps for Austin, Dallas, Houston, San Antonio, and nearby cities.',
    introTitle: 'More than a grill on a deck',
    introParagraphs: [
      'An outdoor kitchen can be a built-in grill with counter space or a full station with sink, refrigeration, storage, and bar seating. Gas, water, electrical, and weatherproof finishes have to work together outdoors.',
      'The strongest projects are planned with the covered patio or pergola they sit under - shade, clearances, and utilities designed as one space.',
      'Share what you want to cook and entertain, then schedule an on-site visit for a written estimate. You decide whether to move forward.',
    ],
    projectTypesTitle: 'Outdoor kitchen projects we commonly handle',
    projectTypes: [
      'Built-in grills with counters and storage',
      'Sinks, refrigeration, and beverage stations',
      'Weatherproof cabinetry and stone or concrete counters',
      'Gas, water, and electrical planning',
      'Layouts coordinated with covered patios or pergolas',
    ],
    whyTitle: 'Why start here',
    whyItems: [
      'Utilities and layout planned together - not pieced late.',
      'On-site review of gas, water, electrical, and shade.',
      'Materials chosen for Texas heat and outdoor exposure.',
      'Written estimate. Free. No obligation.',
      'Coverage across major Texas metros.',
    ],
    processTitle: 'How an outdoor kitchen quote works',
    processSteps: [
      {
        label: '1. Request',
        body: 'List must-haves (grill, sink, fridge, bar) and whether a cover already exists or is planned.',
      },
      {
        label: '2. On-site visit',
        body: 'We review utility access, clearances, and how the kitchen should relate to patio or pergola shade.',
      },
      {
        label: '3. Written estimate',
        body: 'You receive a written estimate for structure, utilities, and finishes. You decide next steps.',
      },
    ],
    galleryEyebrow: 'Outdoor kitchens',
    galleryTitle: 'Cook and gather outside.',
    gallery: [
      {
        src: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=1200&q=80&auto=format&fit=crop',
        alt: 'Outdoor entertaining kitchen area',
      },
      {
        src: 'https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=1200&q=80&auto=format&fit=crop',
        alt: 'Covered patio for outdoor cooking',
      },
      {
        src: 'https://images.unsplash.com/photo-1600585153490-76fb20a32601?w=1200&q=80&auto=format&fit=crop',
        alt: 'Deck adjacent to outdoor kitchen zone',
      },
    ],
    cost: {
      title: 'Typical Texas outdoor kitchen cost context (2026)',
      intro:
        'Planning ranges for Texas market context in 2026 - not a quote for your yard. Appliances, counters, and utility runs are the big cost drivers.',
      rows: [
        {
          tier: 'Basic built-in',
          cost: '$10,000 - $20,000',
          includes: 'Grill, counter, and storage in a simple run.',
        },
        {
          tier: 'Mid-range station',
          cost: '$20,000 - $45,000',
          includes: 'Grill, sink, refrigeration, counters, and storage.',
        },
        {
          tier: 'Full entertainment kitchen',
          cost: '$45,000+',
          includes: 'Premium appliances, bar seating, multiple stations, major utility work.',
        },
      ],
    },
    faqs: [
      {
        question: 'How much does an outdoor kitchen cost in Texas?',
        answer:
          'As of 2026, basic built-ins often land around $10,000-$20,000, mid-range stations around $20,000-$45,000, and full entertainment kitchens $45,000+. Running gas, water, and electrical is a major swing. Your written estimate prices the site.',
      },
      {
        question: 'Do you need gas, water, and electrical?',
        answer:
          'Many outdoor kitchens need at least gas and electrical; sinks add water and drain. Planning utilities early keeps the layout realistic.',
      },
      {
        question: 'Should it sit under a cover?',
        answer:
          'Usually yes. Shade and weather protection keep cooking comfortable and protect appliances. Coordinate the kitchen with a covered patio or pergola when possible.',
      },
      {
        question: 'What counters hold up outdoors in Texas?',
        answer:
          'Granite, stainless, and concrete are common durable choices for heat and sun. Material selection is part of the on-site planning conversation.',
      },
      {
        question: 'Is the estimate free?',
        answer: 'Yes. Free on-site estimate with no obligation.',
      },
    ],
    ctaTitle: 'Ready for an outdoor kitchen quote?',
    ctaLead: 'Free on-site estimate. No obligation.',
    showInNav: true,
  },
];

export const OUTDOOR_SPECIALTY_SLUGS = OUTDOOR_SPECIALTY_SERVICES.map((s) => s.slug);
